import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Dialog, DialogContent, TextField, Box, InputAdornment } from "@mui/material";

import AutocompleteDropdown from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import AddEditButton from "../../../../reusable-components/AddEditButton/AddEditButton";

import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";

import { FieldsContainer } from "./styles";
import { Props, State } from "./interfaces";

import ConfirmationChangesDialog from "../../../../containers/ConfirmationChangesDialog/ConfirmationChangesDialog";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";

import isEqual from "lodash/isEqual";
import { SKILLS_CATEGORIES } from "../../../../utils/constants/constants";
import { SkillsApiService } from "../../../../services/recruiting/candidate/skills/skills-api.service";
import { SkillFilter } from "../../../../models/recruiting/candidate/skills/skill-filter";
import { SkillLevelsApiService } from "../../../../services/recruiting/candidate/skills/skill-levels/skill-levels-api.service";
import { PositionSkillRef } from "../../../../models/recruiting/positions/position-skill/position-skill-ref";
import { savePosition } from "../../../../services/recruiting/positions/positions-api.service";

export const AddEditRequiredPositionSkills = ({
  effect = "add",
  asLink = false,
  linkText,
  item,
  handleNotifyParent,
  buttonVariant,
  children,
  skillCategory,
  position,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.system_settings.tables.candidate_skills";

  const DEFAULT_ITEM: Partial<PositionSkillRef> = useMemo(() => {
    return {
      skill: item?.skill ?? null,
      skillLevel: item?.skillLevel ?? null,
      yearsOfExperience: item?.yearsOfExperience ?? 0,
    };
  }, [item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      requiredPositionSkill: item ?? (DEFAULT_ITEM as PositionSkillRef),
      isCCDOpen: false,
      areFieldsValidated: effect === "edit",
    };
  }, [effect, DEFAULT_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleChangeField = useCallback(
    (field: keyof PositionSkillRef, value: PositionSkillRef[keyof PositionSkillRef]) => {
      setState((state) => ({
        ...state,
        requiredPositionSkill: {
          ...state.requiredPositionSkill,
          [field]: value,
        },
      }));
    },
    []
  );

  const handleChangeYears = ({ target: { value } }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleChangeField("yearsOfExperience", Number(value));
  };

  const handleSave = useCallback(async () => {
    const arrWithSkills =
      effect === "edit"
        ? position.requiredSkills.map((skill) => {
            if (item?.id === skill.id) {
              return { ...skill, ...state.requiredPositionSkill };
            } else {
              return skill;
            }
          })
        : [...position.requiredSkills, { ...state.requiredPositionSkill, id: uuidv4() }];

    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await savePosition({
        ...position,
        requiredSkills: [...arrWithSkills],
      });

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
      }));
      handleNotifyParent();
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.requiredPositionSkill, item?.id, position]);

  const handleDelete = useCallback(async () => {
    const arrWithSkills = position.requiredSkills.filter((skill) => skill.id !== item?.id);

    setState((state) => ({ ...state, loading: true }));

    try {
      const response = await savePosition({
        ...position,
        requiredSkills: [...arrWithSkills],
      });

      if (!response.error) {
        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
          }));
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({
        ...state,
        loading: false,
      }));
      handleNotifyParent();
    }
  }, [INITIAL_STATE, effect, handleNotifyParent, state.requiredPositionSkill, item?.id, position]);

  const handleDiscardChanges = useCallback(() => {
    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  const resetAndCloseDialog = (): void => {
    if (!isEqual(state.requiredPositionSkill, INITIAL_STATE.requiredPositionSkill)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardChanges();
    }
  };

  const areFieldsValidated = useCallback((): boolean => {
    return !!state.requiredPositionSkill.skill && !!state.requiredPositionSkill.skillLevel;
  }, [state.requiredPositionSkill]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: areFieldsValidated(),
    }));
  }, [state.requiredPositionSkill, areFieldsValidated]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        requiredPositionSkill: item,
      }));

    return () => {
      if (effect === "add") setState({ ...INITIAL_STATE });
    };
  }, [item, effect, INITIAL_STATE]);

  const alreadyExistingSkills = useMemo(() => {
    return position.requiredSkills.map((item) => item?.skill?.id);
  }, [position.requiredSkills]);

  return (
    <>
      {children ? (
        <div
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
        >
          {children}
        </div>
      ) : (
        <AddEditButton
          title={t(`buttons.${effect}`)}
          effect={effect}
          asLink={asLink}
          linkText={linkText}
          onClick={() => {
            setState((state) => ({ ...state, isDialogOpen: true }));
          }}
          {...(buttonVariant === "text" && { buttonText: "Edit" })}
        />
      )}
      <Dialog
        open={state.isDialogOpen}
        onClose={resetAndCloseDialog}
        fullWidth
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={effect === "add" ? t(`${tRP}.title_for_add_component`) : t(`${tRP}.title_for_edit_component`)}
            handleClose={resetAndCloseDialog}
          />
          <DialogContent>
            <FieldsContainer>
              <Box>
                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.skills`)}`}
                  field={"skill"}
                  serviceApi={new SkillsApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.requiredPositionSkill.skill}
                  propsFilter={
                    {
                      notIds: alreadyExistingSkills,
                      categoryId: skillCategory.id,
                    } as SkillFilter
                  }
                  required
                />

                <AutocompleteDropdown
                  labelKey={`${t(`${tRP}.fields.skill_level`)}`}
                  field={"skillLevel"}
                  serviceApi={new SkillLevelsApiService()}
                  handleChange={handleChangeField}
                  selectedValue={state.requiredPositionSkill.skillLevel}
                  fieldProp={skillCategory.id === SKILLS_CATEGORIES.language.id ? "altName" : "name"}
                  required
                />
              </Box>

              <Box>
                <TextField
                  label={`${t(`${tRP}.fields.years_of_experience`)}`}
                  type="number"
                  name="yearsOfExperience"
                  value={state.requiredPositionSkill.yearsOfExperience}
                  onChange={handleChangeYears}
                  InputProps={{
                    inputProps: { min: 0, step: 1 },
                    endAdornment: <InputAdornment position="end">{"years"}</InputAdornment>,
                  }}
                  onBlur={handleChangeYears}
                />
              </Box>
            </FieldsContainer>
          </DialogContent>
          <DialogActions
            handleSave={handleSave}
            handleDelete={handleDelete}
            canBeDeleted={!!item?.id}
            disabledSaveButton={!areFieldsValidated()}
            handleCancel={resetAndCloseDialog}
          />
        </LoadingOverlay>
      </Dialog>

      <ConfirmationChangesDialog
        open={state.isCCDOpen}
        handleCancel={() => setState({ ...state, isCCDOpen: false })}
        handleDiscard={handleDiscardChanges}
      />
    </>
  );
};
